import { createContext, useContext, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

// Malay path -> English path. BM is the default language, EN lives under /en.
export const ROUTE_MAP = [
  ["/", "/en"],
  ["/directory", "/en/directory"],
  ["/harga", "/en/pricing"],
  ["/terma", "/en/terms"],
  ["/privasi", "/en/privacy"],
  ["/pemulangan", "/en/refund-policy"],
  ["/hubungi", "/en/contact"],
  ["/submit", "/en/submit"],
  ["/claim", "/en/claim"],
  ["/payment-result", "/en/payment-result"],
  ["/direktori", "/en/directory"],
];

// Dynamic routes are matched by prefix (e.g. /business/:slug).
export const PREFIX_MAP = [
  ["/business/", "/en/business/"],
  ["/claim-card/", "/en/claim-card/"],
  ["/manage/", "/en/manage/"],
  ["/resume/", "/en/resume/"],
];

export const useLang = () => {
  const { pathname } = useLocation();
  return pathname === "/en" || pathname.startsWith("/en/") ? "en" : "ms";
};

/** Pages with language-specific slugs (pSEO landing pages) publish their own counterpart paths. */
const AltPathContext = createContext({ altPaths: null, setAltPaths: () => {} });

export const AltPathProvider = ({ children }) => {
  const [altPaths, setAltPaths] = useState(null);
  const value = useMemo(() => ({ altPaths, setAltPaths }), [altPaths]);
  return <AltPathContext.Provider value={value}>{children}</AltPathContext.Provider>;
};

export const useAltPaths = () => useContext(AltPathContext);

/** URL-safe slug, mirroring the backend slugify used for pSEO paths. */
export const slugifyPath = (value) =>
  (value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

/** Turn a canonical (Malay) path into the path for the active language. */
export const useHref = () => {
  const lang = useLang();
  return (msPath) => {
    if (lang === "ms") return msPath;
    const pair = ROUTE_MAP.find(([ms]) => ms === msPath);
    if (pair) return pair[1];
    const prefix = PREFIX_MAP.find(([ms]) => msPath.startsWith(ms));
    return prefix ? `${prefix[1]}${msPath.slice(prefix[0].length)}` : msPath;
  };
};

/** The same page in the other language, or that language's home page. */
export const counterpartPath = (pathname, target) => {
  const pair = ROUTE_MAP.find(([ms, en]) => ms === pathname || en === pathname);
  if (pair) return target === "en" ? pair[1] : pair[0];
  const prefix = PREFIX_MAP.find(([ms, en]) => pathname.startsWith(ms) || pathname.startsWith(en));
  if (prefix) {
    const from = pathname.startsWith(prefix[1]) ? prefix[1] : prefix[0];
    const to = target === "en" ? prefix[1] : prefix[0];
    return `${to}${pathname.slice(from.length)}`;
  }
  return target === "en" ? "/en" : "/";
};

const MS = {
  "nav.home": "Utama",
  "nav.directory": "Direktori",
  "nav.pricing": "Harga",
  "nav.submit": "Daftar Bisnes",
  "nav.language": "Bahasa",

  "footer.tagline": "Direktori perniagaan tempatan Malaysia — cari, hubungi, dan sokong bisnes berdekatan.",
  "footer.col.directory": "Direktori",
  "footer.col.legal": "Polisi & Bantuan",
  "footer.claim": "Masuk ke Senarai",
  "footer.terms": "Terma Perkhidmatan",
  "footer.privacy": "Dasar Privasi",
  "footer.refund": "Polisi Pemulangan Wang",
  "footer.contact": "Hubungi Kami",
  "footer.rights": "Hak cipta terpelihara.",
  "footer.regno": "No. Pendaftaran",

  "home.eyebrow": "Direktori Perniagaan Malaysia",
  "home.title": "Cari bisnes tempatan di negeri & daerah anda.",
  "home.subtitle":
    "Ribuan perniagaan Malaysia — makanan, bengkel, plumber, kontraktor, tuisyen & freelancer. Hubungi terus melalui telefon atau WhatsApp.",
  "home.stat.listings": "Bisnes tersenarai",
  "home.stat.states": "Negeri",
  "home.stat.categories": "Kategori",
  "home.categories.eyebrow": "Kategori Popular",
  "home.categories.title": "Layari mengikut kategori",
  "home.viewAll": "Lihat semua",
  "home.viewAllListings": "Lihat semua {count} senarai",
  "home.latest.eyebrow": "Terkini",
  "home.latest.title": "Senarai terbaru",

  "search.keyword": "Cari kata kunci / kategori…",
  "search.state": "Negeri (State)",
  "search.allStates": "Semua Negeri",
  "search.district": "Bandar",
  "search.allDistricts": "Semua Bandar",
  "search.category": "Kategori",
  "search.allCategories": "Semua Kategori",
  "search.submit": "Cari",

  "directory.title": "Direktori Perniagaan",  "directory.subtitle": "Tapis mengikut negeri, bandar, kategori atau kata kunci.",
  "directory.results": "{count} hasil ditemui",
  "directory.clear": "Kosongkan tapisan",
  "directory.empty.title": "Tiada bisnes ditemui",
  "directory.empty.body": "Cuba kata kunci lain atau pilih bandar berbeza.",
  "directory.prev": "Sebelum",
  "directory.next": "Seterusnya",
  "directory.page": "Halaman {page} / {pages}",

  "card.photos": "{count} gambar",
  "card.verified": "Nombor disahkan",
  "card.whatsapp": "Chat on WhatsApp",
  "card.cardAlt": "Kad perniagaan {name}",

  "banner.text": "Belum ada kad perniagaan? Reka & cetak kad profesional anda di",
  "banner.cta": "Reka Sekarang",

  "notFound.title": "Halaman tidak ditemui",
  "notFound.body": "Pautan yang anda buka mungkin salah atau sudah dipindahkan.",
  "notFound.directory": "Ke direktori",
  "notFound.pricing": "Lihat harga",

  "pricing.seo.title": "Harga Senaraian — DirektoriBiz.my",
  "pricing.seo.desc":
    "Harga senaraian perniagaan, naik taraf galeri, penampilan khas (featured) dan kombo di DirektoriBiz.my.",
  "pricing.eyebrow": "Harga",
  "pricing.title": "Harga Senaraian DirektoriBiz.my",
  "pricing.subtitle": "Pilih pakej yang sesuai untuk mempromosikan perniagaan anda di DirektoriBiz.my.",
  "pricing.basic.eyebrow": "Senaraian Asas",
  "pricing.basic.desc": "Senaraikan perniagaan anda di DirektoriBiz.my",
  "pricing.basic.note": "Bayaran sekali untuk tempoh senaraian yang ditetapkan.",
  "pricing.basic.cta": "Daftar bisnes sekarang",
  "pricing.gallery.eyebrow": "Naik Taraf Galeri",
  "pricing.gallery.desc":
    "Tampilkan lebih banyak gambar supaya pelanggan dapat mengenali perniagaan, produk atau perkhidmatan anda dengan lebih baik.",
  "pricing.gallery.includes": "Termasuk sehingga {photos} gambar dalam galeri perniagaan.",
  "pricing.gallery.cta": "Naik taraf galeri",
  "pricing.gallery.note": "Boleh diambil semasa daftar (satu bayaran) atau kemudian dari halaman urus senarai.",
  "pricing.featured.eyebrow": "Penampilan Khas (Featured Listing)",
  "pricing.featured.subtitle":
    "Dapatkan lebih banyak pendedahan dengan menjadikan senaraian anda sebagai Penampilan Khas.",
  "pricing.featured.cta": "Naik taraf",
  "pricing.featured.note":
    "Penampilan Khas membantu senaraian anda mendapat kedudukan dan perhatian yang lebih baik dalam DirektoriBiz.my.",
  "pricing.bundle.eyebrow": "Kombo Penampilan Khas + Galeri",
  "pricing.bundle.subtitle":
    "Pilihan lebih berbaloi untuk peniaga yang mahu lebih banyak gambar dan pendedahan tambahan.",
  "pricing.bundle.save": "jimat",
  "pricing.bundle.featuredFor": "Penampilan Khas selama {label}",
  "pricing.bundle.galleryUpTo": "Galeri sehingga {photos} gambar",
  "pricing.bundle.cta": "Ambil pakej ini",
  "pricing.bundle.note": "Pilihan terbaik untuk promosi jangka panjang.",
  "pricing.notes.eyebrow": "Nota",
  "pricing.notes.basic": "Harga Asas ialah {price}.",
  "pricing.notes.change": "Harga dan pakej tertakluk kepada perubahan dari semasa ke semasa.",

  "contact.seo.title": "Hubungi Kami — DirektoriBiz.my",
  "contact.seo.desc": "Hubungi pasukan DirektoriBiz.my untuk pertanyaan senaraian, pembayaran atau kerjasama.",
  "contact.eyebrow": "Hubungi Kami",
  "contact.title": "Ada pertanyaan?",
  "contact.intro":
    "Pertanyaan tentang senaraian, pembayaran, pakej promosi atau kerjasama — hantar mesej dan pasukan kami akan membalas dalam masa 1–2 hari bekerja.",
  "contact.emailLabel": "Emel",
  "contact.field.name": "Nama penuh",
  "contact.field.email": "Emel",
  "contact.field.phone": "No. telefon (pilihan)",
  "contact.field.subject": "Subjek",
  "contact.field.message": "Mesej",
  "contact.submit": "Hantar mesej",
  "contact.sent.title": "Mesej dihantar!",
  "contact.sent.body": "Terima kasih, {name}. Kami akan menghubungi anda melalui emel.",
  "contact.sent.again": "Hantar mesej lain",
  "contact.toast.sent": "Mesej anda telah dihantar. Kami akan balas secepat mungkin.",
  "contact.you": "anda",

  "submit.preview.title": "Pratonton kad anda",
  "submit.preview.body": "Setiap kategori ada warna kad tersendiri. Begini rupa senarai anda dalam direktori.",
  "submit.preview.placeholder": "Nama Perniagaan Anda",
  "submit.eyebrow": "Daftar Bisnes",
  "submit.title": "Muat Naik Maklumat Perniagaan",
  "submit.intro":
    "Muat naik gambar atau dokumen yang mengandungi maklumat perniagaan. AI akan membaca dan mengisi borang secara automatik. (Gambar/dokumen ini tidak akan ditunjukkan di laman web)",
  "submit.paywall.on": "Bayaran satu kali {price} melalui toyyibPay (FPX) diperlukan sebelum senarai anda LIVE.",
  "submit.paywall.off": "Mod Promosi Percuma aktif — senarai anda akan LIVE serta-merta tanpa bayaran.",
  "submit.docs.label": "Gambar / Dokumen Perniagaan",
  "submit.docs.hint":
    "Kad perniagaan, flyer, poster, resit atau invois, letterhead, screenshot, papan tanda, dokumen bercetak atau tulisan tangan yang jelas. Boleh imbas sehingga 2 dokumen (contoh: kad + papan tanda) untuk maklumat lebih lengkap.",
  "submit.docs.formats": "JPG, PNG atau WEBP (maks 8MB)",
  "submit.docs.reading": "Sedang membaca maklumat…",
  "submit.docs.readingShort": "Sedang membaca…",
  "submit.docs.remove": "Buang",
  "submit.docs.camera": "Imbas dengan Kamera",
  "submit.docs.pick": "Pilih Gambar atau Dokumen",
  "submit.docs.second": "Tambah dokumen kedua ({n}/{max})",
  "submit.docs.limit": "Had {max} dokumen dicapai",
  "submit.docs.cameraNote": "Guna kamera telefon untuk imbas kad, flyer atau papan tanda terus.",
  "submit.docs.privacyNote":
    "Gambar/dokumen ini hanya digunakan untuk mengisi borang — ia tidak dipaparkan kepada umum.",
  "submit.docs.aiTags": "Kata kunci AI",
  "submit.docs.alt": "Dokumen {n}",
  "submit.field.name": "Nama Perniagaan *",
  "submit.field.phone": "Nombor Telefon *",
  "submit.field.email": "Emel (untuk resit)",
  "submit.field.state": "Negeri (State) *",
  "submit.field.district": "Bandar / Daerah *",
  "submit.field.category": "Kategori Bisnes *",
  "submit.field.address": "Alamat (No. Premis & Nama Jalan)",
  "submit.field.postcode": "Poskod",
  "submit.field.description": "Keterangan ringkas",
  "submit.select.state": "Pilih negeri",
  "submit.select.district": "Pilih bandar",
  "submit.select.category": "Pilih kategori",
  "submit.postcodeNote": "Poskod membantu peta menanda lot kedai anda dengan tepat.",
  "submit.map.label": "Lokasi pada peta",
  "submit.photo.label": "Gambar Perniagaan (Pilihan)",
  "submit.photo.hint":
    "Muat naik satu gambar premis, produk atau servis anda untuk dipaparkan pada halaman bisnes.",
  "submit.photo.uploading": "Memuat naik…",
  "submit.photo.change": "Tukar gambar perniagaan",
  "submit.photo.pick": "Pilih gambar perniagaan",
  "submit.photo.alt": "Gambar tambahan",
  "submit.addon.label": "Pakej Promosi (Pilihan)",
  "submit.addon.hint": "Ambil sekarang dan bayar sekali sahaja bersama harga asas.",
  "addon.basic": "Asas sahaja",
  "addon.basic.note": "Senarai anda LIVE tanpa promosi tambahan",
  "addon.gallery": "Naik Taraf Galeri",
  "addon.gallery.note": "Sehingga {n} gambar galeri",
  "addon.featured": "Penampilan Khas {label}",
  "addon.featured.note": "Muncul di bahagian atas hasil carian daerah",
  "addon.bundle": "Kombo {label} + Galeri",
  "addon.bundle.note": "Featured + galeri, jimat {saving}",
  "submit.coupon.label": "Kupon (jika ada)",
  "submit.coupon.hint": "Kupon senaraian percuma hanya sah untuk pakej Asas sahaja.",
  "submit.coupon.placeholder": "Contoh: FREEA1B2C3",
  "submit.coupon.apply": "Guna",
  "submit.coupon.applied": "Kupon {code} digunakan — tiada bayaran diperlukan.",
  "submit.summary.base": "Harga Asas",
  "submit.summary.total": "Jumlah bayaran",
  "submit.summary.saving": "Jimat {saving} berbanding beli berasingan.",
  "submit.summary.note": "Satu bayaran sahaja melalui FPX (toyyibPay). Tiada langganan berulang.",
  "submit.cta.pay": "Bayar RM{amount} & Terbitkan",
  "submit.cta.free": "Terbitkan Senarai",
  "submit.toast.maxDocs": "Maksimum {max} dokumen sahaja.",
  "submit.toast.ocrFail": "Fail dimuat naik, tetapi maklumat tidak dapat dibaca. Sila isi borang secara manual.",
  "submit.toast.ocrFound": "{n} maklumat berjaya dikesan. Sila semak sebelum menghantar.",
  "submit.toast.ocrNone": "Tiada maklumat jelas dikesan. Sila isi borang secara manual.",
  "submit.toast.photoSize": "Gambar mesti kurang daripada 8MB",
  "submit.toast.photoUploaded": "Gambar tambahan dimuat naik",
  "submit.toast.required": "Sila lengkapkan semua medan wajib.",
  "submit.toast.live": "Tersenarai! Bisnes anda kini LIVE.",
  "submit.toast.couponOk": "Kupon sah — senaraian Asas anda percuma!",
  "submit.toast.couponRemoved": "Kupon dibuang — kupon hanya sah untuk senaraian Asas.",
  "submit.done.title": "Berjaya didaftarkan!",
  "submit.done.body":
    "Status: {status}. Terima kasih kerana menyenaraikan bisnes anda di DirektoriBiz.my.",
  "submit.done.viewDir": "Lihat Direktori",
  "submit.done.again": "Daftar lagi satu",

  "detail.notFound.title": "Perniagaan tidak ditemui",
  "detail.notFound.body": "Senarai ini mungkin telah dipadam atau belum diluluskan.",
  "detail.backToDir": "Kembali ke direktori",
  "detail.back": "Direktori",
  "detail.share": "Kongsi",
  "detail.shareCopied": "Pautan kongsi disalin!",
  "detail.claim.title": "Ini bisnes anda?",
  "detail.claim.body":
    "Tuntut senarai ini untuk mengurus maklumat, gambar dan lokasi anda sendiri. Masukkan emel dan selesaikan bayaran senarai — pautan urus akan dihantar ke emel anda.",
  "detail.claim.cta": "Tuntut senarai ini",
  "detail.tags": "Kata kunci",
  "detail.gallery": "Galeri",
  "detail.related": "{category} lain di {state}",
  "detail.photoAlt": "{name} gambar {n}",
  "detail.close": "Tutup",
  "detail.prevPhoto": "Gambar sebelum",
  "detail.nextPhoto": "Gambar seterusnya",
  "detail.photoN": "Gambar {n}",
  "detail.seo.title": "{name} — {category} di {district}, {state} | Direktori Perniagaan Malaysia",
  "detail.seo.fallbackTitle": "Perniagaan | Direktori Perniagaan Malaysia",
  "detail.seo.desc": "{name}, {category} di {district}, {state}. Hubungi {phone} atau WhatsApp terus.",

  "claim.title": "Masuk ke senarai anda",
  "claim.intro":
    "Halaman ini untuk pemilik yang sudah ada senarai di DirektoriBiz.my — untuk mengemas kini maklumat atau mengambil pakej naik taraf.",
  "claim.pkg.title": "Anda memilih pakej: {pakej}",
  "claim.pkg.body":
    "Pakej naik taraf perlu dilekatkan pada satu senarai. Jadi kami perlu tahu senarai mana milik anda dahulu — masukkan nombor telefon yang tersenarai di bawah, dan pakej boleh dibayar dari halaman urus senarai anda.",
  "claim.have.title": "Sudah ada senarai?",
  "claim.have.body":
    "Masukkan nombor telefon yang tersenarai di bawah. Kami hantar kod 6 angka ke emel pemilik, kemudian anda terus masuk ke halaman urus — tiada borang panjang.",
  "claim.none.title": "Belum ada senarai?",
  "claim.none.body":
    "Anda perlu daftar bisnes dahulu (borang pendek + imbas dokumen), kemudian pakej boleh diambil.",
  "claim.none.cta": "Daftar bisnes dahulu",
  "claim.phone": "Nombor Telefon",
  "claim.lookup": "Cari senarai",
  "claim.codeTo": "Kod ke {email}",
  "claim.adminNeeded": "Perlu kelulusan admin",
  "claim.select": "Tuntut ini",
  "claim.needEmail.body":
    "Senarai {name} belum ada emel pemilik. Masukkan emel anda — admin akan sahkan pemilikan secara manual.",
  "claim.email": "Emel anda",
  "claim.requestReview": "Hantar permintaan",
  "claim.adminReview.body":
    "Permintaan anda untuk {name} telah dihantar. Admin akan menyemak dan menghubungi anda melalui emel yang diberikan.",
  "claim.code.sent": "Kod 6 angka telah dihantar ke {email}. Sah untuk 15 minit.",
  "claim.code.label": "Kod pengesahan",
  "claim.verify": "Sahkan",
  "claim.toast.none": "Tiada senarai dengan nombor telefon ini.",
  "claim.toast.sent": "Permintaan dihantar untuk kelulusan admin.",
  "claim.toast.codeSent": "Kod dihantar ke {email}",
  "claim.toast.verified": "Pemilikan disahkan!",

  "claimcard.title": "Tuntut {name}",
  "claimcard.intro":
    "Masukkan emel anda dan selesaikan bayaran senarai {price}. Selepas bayaran berjaya, pautan urus senarai dan resit akan dihantar ke emel tersebut.",
  "claimcard.email": "Emel anda *",
  "claimcard.emailNote": "Pautan urus senarai dan resit akan dihantar ke emel ini sahaja. Sah untuk 7 hari.",
  "claimcard.priceLabel": "Bayaran tuntutan",
  "claimcard.cta": "Bayar {price} & tuntut",
  "claimcard.preparing": "Menyediakan bayaran…",
  "claimcard.note": "Bayaran sekali melalui FPX (toyyibPay). Tiada langganan berulang.",
  "claimcard.notFound": "Senarai tidak ditemui",

  "manage.denied.title": "Sahkan pemilikan dahulu",
  "manage.denied.body":
    "Sesi pemilikan tiada atau telah tamat. Tuntut semula senarai anda menggunakan nombor telefon.",
  "manage.denied.cta": "Ke halaman tuntutan",
  "manage.eyebrow": "Urus senarai",
  "manage.viewPublic": "Lihat halaman awam",
  "manage.share.whatsapp": "Kongsi ke WhatsApp",
  "manage.share.copy": "Salin mesej",
  "manage.share.note": "Pilih grup atau kontak — mesej dan kad bergambar sudah siap.",
  "manage.share.copied": "Mesej kongsi disalin!",
  "manage.share.copyFail": "Tidak dapat menyalin. Cuba tekan butang WhatsApp.",
  "manage.share.msg": "*{name}* — {category} di {district}, {state}\n📞 {phone}\n📍 Lihat lokasi & gambar: {url}",
  "manage.upsell.eyebrow": "Tahniah, senarai ini milik anda",
  "manage.upsell.title": "Naik taraf ke Featured — {price}",
  "manage.upsell.countdown": "Tawaran pemilik baru — tinggal {hours} jam lagi",
  "manage.upsell.b1": "Muncul di bahagian paling atas hasil carian daerah anda selama {days} hari",
  "manage.upsell.b2": "Lencana Featured pada setiap kad senarai — lebih dipercayai pelanggan",
  "manage.upsell.b3": "Bayar sekali melalui FPX, tiada langganan berulang",
  "manage.upsell.cta": "Naik taraf {price} sekarang",
  "manage.upsell.later": "Nanti dulu",
  "manage.promote.title": "Featured Placement",
  "manage.promote.daysLeft": "{days} hari lagi · tamat {date}",
  "manage.promote.activeBody": "Senarai anda kini Featured sehingga {date}. Bayar lagi untuk melanjutkan.",
  "manage.promote.idleBody": "Naik ke bahagian atas hasil carian daerah anda — pilih tempoh di bawah.",
  "manage.promote.offer": "Harga tawaran {price} (biasa {normal}) — tinggal {hours} jam",
  "manage.promote.daysTop": "{days} hari di atas",
  "manage.promote.newOwnerOffer": "tawaran pemilik baru",
  "manage.promote.pay": "Bayar {price}",
  "manage.promote.note": "Bayar sekali melalui FPX — tiada langganan berulang.",
  "manage.bundle.badge": "Pakej bundle — jimat {saving}",
  "manage.bundle.title": "Featured {label} + Galeri {photos} gambar = ",
  "manage.bundle.body":
    "Beli berasingan {separate} ({featured} + {gallery}). Galeri kekal selamanya walaupun Featured tamat.",
  "manage.bundle.cta": "Ambil bundle {price}",
  "manage.gallery.title": "Galeri gambar",
  "manage.gallery.unlocked": "Galeri dibuka — sehingga {limit} gambar. Gambar dipaparkan di halaman awam.",
  "manage.gallery.locked": "Pelan percuma: {free} gambar. Naik taraf untuk sehingga {paid} gambar.",
  "manage.gallery.usage": "{used} / {limit} gambar digunakan",
  "manage.gallery.add": "Tambah gambar",
  "manage.gallery.upgradeTitle": "Naik taraf galeri — {price} sekali sahaja",
  "manage.gallery.upgradeBody":
    "Buka sehingga {photos} gambar untuk senarai ini (kekal selamanya). Senarai bergambar lebih dipercayai dan lebih banyak menerima klik WhatsApp.",
  "manage.gallery.upgradeCta": "Bayar {price} & buka galeri",
  "manage.gallery.empty": "Belum ada gambar. Tambah gambar untuk menarik lebih banyak pelanggan.",
  "manage.gallery.main": "Utama",
  "manage.gallery.remove": "Buang gambar",
  "manage.gallery.moveLeft": "Alih ke kiri",
  "manage.gallery.moveRight": "Alih ke kanan",
  "manage.gallery.photoAlt": "Gambar {n}",
  "manage.form.phone": "Nombor Telefon",
  "manage.form.category": "Kategori",
  "manage.form.state": "Negeri",
  "manage.form.district": "Bandar",
  "manage.form.address": "Alamat (No. Premis & Nama Jalan)",
  "manage.form.postcode": "Poskod",
  "manage.form.addressNote": "Alamat dan poskod digunakan untuk menanda lokasi tepat anda di peta.",
  "manage.form.map": "Lokasi pada peta",
  "manage.form.description": "Keterangan",
  "manage.form.save": "Simpan perubahan",
  "manage.toast.saved": "Senarai anda dikemas kini.",
  "manage.toast.photoAdded": "Gambar ditambah.",
  "manage.toast.photoRemoved": "Gambar dibuang.",
  "manage.toast.renewing": "Membuka halaman pembayaran untuk sambung Featured…",

  "pay.success.title": "Pembayaran berjaya!",
  "pay.fail.title": "Pembayaran tidak berjaya",
  "pay.success.body":
    "Pembayaran FPX melalui toyyibPay diterima. Senarai bisnes anda kini LIVE dalam direktori. Resit telah dihantar ke emel anda.",
  "pay.fail.body": "Bil toyyibPay anda belum dibayar. Anda boleh sambung bayaran tanpa mengisi borang semula.",
  "pay.billId": "Bill ID",
  "pay.viewDir": "Lihat Direktori",
  "pay.resume": "Sambung bayaran",
  "pay.backForm": "Kembali ke borang",

  "resume.invalid": "Pautan tidak sah",
  "resume.newSubmit": "Daftar bisnes baru",
  "resume.live.title": "Senarai anda sudah LIVE",
  "resume.pending.title": "Sambung bayaran anda",
  "resume.live.body": "Tiada bayaran diperlukan. Senarai anda sudah kelihatan dalam direktori.",
  "resume.pending.body":
    "Tidak perlu isi borang semula — teruskan pembayaran untuk menerbitkan senarai anda.",
  "resume.row.business": "Perniagaan",
  "resume.row.category": "Kategori",
  "resume.row.location": "Lokasi",
  "resume.row.amount": "Jumlah",
  "resume.row.status": "Status",
  "resume.viewListing": "Lihat senarai anda",
  "resume.pay": "Bayar {price} melalui toyyibPay",
  "resume.browse": "Layari direktori",
  "resume.toast.live": "Senarai anda kini LIVE!",

  "map.hint": "Pilih negeri dan bandar dahulu untuk menunjukkan peta.",
  "map.search": "Cari nama bandar (cth: Alor Setar)",
  "map.toast.short": "Taip sekurang-kurangnya 3 huruf.",
  "map.toast.notFound": "Tempat tidak ditemui. Cuba nama tempat lain.",
  "map.toast.found": "Lokasi ditemui — laraskan pin jika perlu.",
  "map.searching": "Mencari lokasi…",
  "map.failed": "Peta tidak dapat mencari alamat ini. Anda masih boleh hantar borang tanpa pin.",
  "map.adjust": "Laraskan pin jika lokasi yang ditunjukkan tidak tepat — seret pin ke premis anda.",
  "map.location": "Lokasi",
  "map.directions": "Dapatkan arah (Google Maps)",
  "map.loading": "Memuatkan peta…",
  "map.unavailable": "Peta tidak dapat dimuatkan untuk lokasi ini. Cuba buka dalam peta luaran.",
  "map.searchBtn": "Cari",
  "submit.seo.title": "Daftar Bisnes — DirektoriBiz.my",
  "submit.seo.desc": "Senaraikan perniagaan anda di DirektoriBiz.my — borang pendek, imbasan AI, dan bayaran sekali.",
  "claim.seo.title": "Masuk ke Senarai Anda — DirektoriBiz.my",
  "claim.seo.desc": "Pemilik boleh masuk ke senarai mereka untuk mengemas kini maklumat atau mengambil pakej naik taraf.",
  "manage.seo.title": "Urus Senarai — DirektoriBiz.my",
  "pay.seo.title": "Keputusan Pembayaran — DirektoriBiz.my",
  "resume.seo.title": "Sambung Bayaran — DirektoriBiz.my",
  "claimcard.seo.title": "Tuntut Senarai — DirektoriBiz.my",
  "seo.notFound.title": "Lokasi tidak dikenali",
  "seo.notFound.body": "Pautan kategori atau daerah ini tidak sah. Cuba cari dari halaman direktori.",
  "seo.related.categories": "Kategori lain di {place}",
  "seo.related.districts": "Daerah lain di {state}",
  "seo.browse.title": "Layari direktori mengikut lokasi",
  "directory.nearby.title": "Berdekatan dalam daerah {district}",
  "directory.nearby.body": "Bisnes di bandar dan pekan berhampiran yang berkongsi daerah yang sama.",
  "seo.browse.body": "Halaman khas untuk setiap negeri dan daerah di Malaysia — pilih kawasan anda.",
  "legal.updated": "Kemas kini terakhir",
  "legal.regno": "No. Pendaftaran",
  "legal.email": "Emel",
};

const EN = {
  "nav.home": "Home",
  "nav.directory": "Directory",
  "nav.pricing": "Pricing",
  "nav.submit": "List Business",
  "nav.language": "Language",

  "footer.tagline": "Malaysia's local business directory — find, contact and support businesses near you.",
  "footer.col.directory": "Directory",
  "footer.col.legal": "Policies & Support",
  "footer.claim": "Access Your Listing",
  "footer.terms": "Terms of Service",
  "footer.privacy": "Privacy Policy",
  "footer.refund": "Refund Policy",
  "footer.contact": "Contact Us",
  "footer.rights": "All rights reserved.",
  "footer.regno": "Registration No.",

  "home.eyebrow": "Malaysia Business Directory",
  "home.title": "Find local businesses in your state & district.",
  "home.subtitle":
    "Thousands of Malaysian businesses — food, workshops, plumbers, contractors, tuition centres & freelancers. Contact them straight away by phone or WhatsApp.",
  "home.stat.listings": "Businesses listed",
  "home.stat.states": "States",
  "home.stat.categories": "Categories",
  "home.categories.eyebrow": "Popular Categories",
  "home.categories.title": "Browse by category",
  "home.viewAll": "View all",
  "home.viewAllListings": "View all {count} listings",
  "home.latest.eyebrow": "Latest",
  "home.latest.title": "Newest listings",

  "search.keyword": "Search keyword / category…",
  "search.state": "State",
  "search.allStates": "All states",
  "search.district": "Town / City",
  "search.allDistricts": "All towns",
  "search.category": "Category",
  "search.allCategories": "All categories",
  "search.submit": "Search",

  "directory.title": "Business Directory",
  "directory.subtitle": "Filter by state, town, category or keyword.",
  "directory.results": "{count} results found",
  "directory.clear": "Clear filters",
  "directory.empty.title": "No businesses found",
  "directory.empty.body": "Try a different keyword or pick another town.",
  "directory.prev": "Previous",
  "directory.next": "Next",
  "directory.page": "Page {page} of {pages}",

  "card.photos": "{count} photos",
  "card.verified": "Verified number",
  "card.whatsapp": "Chat on WhatsApp",
  "card.cardAlt": "{name} business card",

  "banner.text": "No business card yet? Design & print your professional card at",
  "banner.cta": "Design Now",

  "notFound.title": "Page not found",
  "notFound.body": "The link you opened may be wrong or the page has moved.",
  "notFound.directory": "Go to directory",
  "notFound.pricing": "View pricing",

  "pricing.seo.title": "Listing Pricing — DirektoriBiz.my",
  "pricing.seo.desc":
    "Business listing pricing, gallery upgrade, featured placement and combo packages on DirektoriBiz.my.",
  "pricing.eyebrow": "Pricing",
  "pricing.title": "DirektoriBiz.my Listing Pricing",
  "pricing.subtitle": "Choose the package that suits you best to promote your business on DirektoriBiz.my.",
  "pricing.basic.eyebrow": "Basic Listing",
  "pricing.basic.desc": "List your business on DirektoriBiz.my",
  "pricing.basic.note": "One-off payment for the stated listing period.",
  "pricing.basic.cta": "List your business now",
  "pricing.gallery.eyebrow": "Gallery Upgrade",
  "pricing.gallery.desc":
    "Show more photos so customers get to know your business, products or services much better.",
  "pricing.gallery.includes": "Includes up to {photos} photos in your business gallery.",
  "pricing.gallery.cta": "Upgrade gallery",
  "pricing.gallery.note": "Add it during registration (single payment) or later from your listing manager.",
  "pricing.featured.eyebrow": "Featured Listing",
  "pricing.featured.subtitle": "Get far more exposure by turning your listing into a Featured listing.",
  "pricing.featured.cta": "Upgrade",
  "pricing.featured.note":
    "Featured placement helps your listing rank higher and get more attention on DirektoriBiz.my.",
  "pricing.bundle.eyebrow": "Featured + Gallery Combo",
  "pricing.bundle.subtitle": "Better value for business owners who want more photos and extra exposure.",
  "pricing.bundle.save": "save",
  "pricing.bundle.featuredFor": "Featured for {label}",
  "pricing.bundle.galleryUpTo": "Gallery of up to {photos} photos",
  "pricing.bundle.cta": "Get this package",
  "pricing.bundle.note": "The best choice for long-term promotion.",
  "pricing.notes.eyebrow": "Notes",
  "pricing.notes.basic": "The Basic price is {price}.",
  "pricing.notes.change": "Prices and packages may change from time to time.",

  "contact.seo.title": "Contact Us — DirektoriBiz.my",
  "contact.seo.desc": "Contact the DirektoriBiz.my team about listings, payments or partnerships.",
  "contact.eyebrow": "Contact Us",
  "contact.title": "Have a question?",
  "contact.intro":
    "Questions about listings, payments, promo packages or partnerships — send us a message and our team will reply within 1–2 working days.",
  "contact.emailLabel": "Email",
  "contact.field.name": "Full name",
  "contact.field.email": "Email",
  "contact.field.phone": "Phone number (optional)",
  "contact.field.subject": "Subject",
  "contact.field.message": "Message",
  "contact.submit": "Send message",
  "contact.sent.title": "Message sent!",
  "contact.sent.body": "Thank you, {name}. We will get back to you by email.",
  "contact.sent.again": "Send another message",
  "contact.toast.sent": "Your message has been sent. We will reply as soon as possible.",
  "contact.you": "there",

  "submit.preview.title": "Preview your card",
  "submit.preview.body": "Every category has its own card colour. This is how your listing will look in the directory.",
  "submit.preview.placeholder": "Your Business Name",
  "submit.eyebrow": "List Business",
  "submit.title": "Upload Your Business Details",
  "submit.intro":
    "Upload a photo or document that contains your business details. Our AI reads it and fills in the form automatically. (This image/document is never shown on the website)",
  "submit.paywall.on": "A one-off payment of {price} via toyyibPay (FPX) is required before your listing goes LIVE.",
  "submit.paywall.off": "Free Promotion Mode is active — your listing goes LIVE instantly, no payment needed.",
  "submit.docs.label": "Business Photo / Document",
  "submit.docs.hint":
    "Business card, flyer, poster, receipt or invoice, letterhead, screenshot, signboard, printed or clearly handwritten document. You can scan up to 2 documents (e.g. card + signboard) for more complete details.",
  "submit.docs.formats": "JPG, PNG or WEBP (max 8MB)",
  "submit.docs.reading": "Reading your details…",
  "submit.docs.readingShort": "Reading…",
  "submit.docs.remove": "Remove",
  "submit.docs.camera": "Scan with Camera",
  "submit.docs.pick": "Choose Photo or Document",
  "submit.docs.second": "Add a second document ({n}/{max})",
  "submit.docs.limit": "Limit of {max} documents reached",
  "submit.docs.cameraNote": "Use your phone camera to scan a card, flyer or signboard directly.",
  "submit.docs.privacyNote":
    "This image/document is only used to fill in the form — it is never shown publicly.",
  "submit.docs.aiTags": "AI keywords",
  "submit.docs.alt": "Document {n}",
  "submit.field.name": "Business Name *",
  "submit.field.phone": "Phone Number *",
  "submit.field.email": "Email (for the receipt)",
  "submit.field.state": "State *",
  "submit.field.district": "Town / City *",
  "submit.field.category": "Business Category *",
  "submit.field.address": "Address (Unit No. & Street Name)",
  "submit.field.postcode": "Postcode",
  "submit.field.description": "Short description",
  "submit.select.state": "Select a state",
  "submit.select.district": "Select a town",
  "submit.select.category": "Select a category",
  "submit.postcodeNote": "A postcode helps the map pin your shop lot accurately.",
  "submit.map.label": "Location on the map",
  "submit.photo.label": "Business Photo (Optional)",
  "submit.photo.hint":
    "Upload one photo of your premises, product or service to show on your business page.",
  "submit.photo.uploading": "Uploading…",
  "submit.photo.change": "Change business photo",
  "submit.photo.pick": "Choose a business photo",
  "submit.photo.alt": "Additional photo",
  "submit.addon.label": "Promo Package (Optional)",
  "submit.addon.hint": "Add it now and pay just once together with the basic price.",
  "addon.basic": "Basic only",
  "addon.basic.note": "Your listing goes LIVE with no extra promotion",
  "addon.gallery": "Gallery Upgrade",
  "addon.gallery.note": "Up to {n} gallery photos",
  "addon.featured": "Featured Placement {label}",
  "addon.featured.note": "Appear at the top of district search results",
  "addon.bundle": "Combo {label} + Gallery",
  "addon.bundle.note": "Featured + gallery, save {saving}",
  "submit.coupon.label": "Coupon (if you have one)",
  "submit.coupon.hint": "A free-listing coupon is valid for the Basic package only.",
  "submit.coupon.placeholder": "Example: FREEA1B2C3",
  "submit.coupon.apply": "Apply",
  "submit.coupon.applied": "Coupon {code} applied — no payment required.",
  "submit.summary.base": "Basic price",
  "submit.summary.total": "Total payable",
  "submit.summary.saving": "Save {saving} compared with buying separately.",
  "submit.summary.note": "A single payment via FPX (toyyibPay). No recurring subscription.",
  "submit.cta.pay": "Pay RM{amount} & Publish",
  "submit.cta.free": "Publish Listing",
  "submit.toast.maxDocs": "Maximum {max} documents only.",
  "submit.toast.ocrFail": "File uploaded, but we could not read the details. Please fill in the form manually.",
  "submit.toast.ocrFound": "{n} details detected. Please check them before submitting.",
  "submit.toast.ocrNone": "No clear details detected. Please fill in the form manually.",
  "submit.toast.photoSize": "The photo must be smaller than 8MB",
  "submit.toast.photoUploaded": "Additional photo uploaded",
  "submit.toast.required": "Please complete all required fields.",
  "submit.toast.live": "Listed! Your business is now LIVE.",
  "submit.toast.couponOk": "Coupon valid — your Basic listing is free!",
  "submit.toast.couponRemoved": "Coupon removed — coupons are valid for Basic listings only.",
  "submit.done.title": "Successfully registered!",
  "submit.done.body": "Status: {status}. Thank you for listing your business on DirektoriBiz.my.",
  "submit.done.viewDir": "View Directory",
  "submit.done.again": "Register another one",

  "detail.notFound.title": "Business not found",
  "detail.notFound.body": "This listing may have been removed or is not approved yet.",
  "detail.backToDir": "Back to directory",
  "detail.back": "Directory",
  "detail.share": "Share",
  "detail.shareCopied": "Share link copied!",
  "detail.claim.title": "Is this your business?",
  "detail.claim.body":
    "Claim this listing to manage your own details, photos and location. Enter your email and complete the listing payment — the management link will be emailed to you.",
  "detail.claim.cta": "Claim this listing",
  "detail.tags": "Keywords",
  "detail.gallery": "Gallery",
  "detail.related": "Other {category} in {state}",
  "detail.photoAlt": "{name} photo {n}",
  "detail.close": "Close",
  "detail.prevPhoto": "Previous photo",
  "detail.nextPhoto": "Next photo",
  "detail.photoN": "Photo {n}",
  "detail.seo.title": "{name} — {category} in {district}, {state} | Malaysia Business Directory",
  "detail.seo.fallbackTitle": "Business | Malaysia Business Directory",
  "detail.seo.desc": "{name}, {category} in {district}, {state}. Call {phone} or message on WhatsApp.",

  "claim.title": "Access your listing",
  "claim.intro":
    "This page is for owners who already have a listing on DirektoriBiz.my — to update details or buy an upgrade package.",
  "claim.pkg.title": "You selected the package: {pakej}",
  "claim.pkg.body":
    "An upgrade package must be attached to a listing, so we need to know which listing is yours first. Enter your listed phone number below, then you can pay for the package from your listing manager.",
  "claim.have.title": "Already have a listing?",
  "claim.have.body":
    "Enter your listed phone number below. We email a 6-digit code to the owner's address, then you go straight to the manage page — no long forms.",
  "claim.none.title": "No listing yet?",
  "claim.none.body":
    "You need to register your business first (a short form + document scan), after which you can add a package.",
  "claim.none.cta": "Register your business first",
  "claim.phone": "Phone Number",
  "claim.lookup": "Find my listing",
  "claim.codeTo": "Code to {email}",
  "claim.adminNeeded": "Needs admin approval",
  "claim.select": "Claim this one",
  "claim.needEmail.body":
    "The listing {name} has no owner email yet. Enter your email — an admin will verify ownership manually.",
  "claim.email": "Your email",
  "claim.requestReview": "Send request",
  "claim.adminReview.body":
    "Your request for {name} has been sent. An admin will review it and contact you at the email you provided.",
  "claim.code.sent": "A 6-digit code has been sent to {email}. Valid for 15 minutes.",
  "claim.code.label": "Verification code",
  "claim.verify": "Verify",
  "claim.toast.none": "No listing found with this phone number.",
  "claim.toast.sent": "Request sent for admin approval.",
  "claim.toast.codeSent": "Code sent to {email}",
  "claim.toast.verified": "Ownership verified!",

  "claimcard.title": "Claim {name}",
  "claimcard.intro":
    "Enter your email and complete the {price} listing payment. Once payment succeeds, the management link and receipt are emailed to that address.",
  "claimcard.email": "Your email *",
  "claimcard.emailNote": "The management link and receipt go to this email only. Valid for 7 days.",
  "claimcard.priceLabel": "Claim payment",
  "claimcard.cta": "Pay {price} & claim",
  "claimcard.preparing": "Preparing payment…",
  "claimcard.note": "A single payment via FPX (toyyibPay). No recurring subscription.",
  "claimcard.notFound": "Listing not found",

  "manage.denied.title": "Verify ownership first",
  "manage.denied.body":
    "Your owner session is missing or has expired. Claim your listing again using your phone number.",
  "manage.denied.cta": "Go to the claim page",
  "manage.eyebrow": "Manage listing",
  "manage.viewPublic": "View public page",
  "manage.share.whatsapp": "Share on WhatsApp",
  "manage.share.copy": "Copy message",
  "manage.share.note": "Pick a group or contact — the message and photo card are ready to go.",
  "manage.share.copied": "Share message copied!",
  "manage.share.copyFail": "Could not copy. Try the WhatsApp button instead.",
  "manage.share.msg": "*{name}* — {category} in {district}, {state}\n📞 {phone}\n📍 See location & photos: {url}",
  "manage.upsell.eyebrow": "Congratulations, this listing is yours",
  "manage.upsell.title": "Upgrade to Featured — {price}",
  "manage.upsell.countdown": "New owner offer — {hours} hours left",
  "manage.upsell.b1": "Appear right at the top of your district's search results for {days} days",
  "manage.upsell.b2": "A Featured badge on every listing card — more trust from customers",
  "manage.upsell.b3": "Pay once via FPX, no recurring subscription",
  "manage.upsell.cta": "Upgrade for {price} now",
  "manage.upsell.later": "Maybe later",
  "manage.promote.title": "Featured Placement",
  "manage.promote.daysLeft": "{days} days left · ends {date}",
  "manage.promote.activeBody": "Your listing is Featured until {date}. Pay again to extend it.",
  "manage.promote.idleBody": "Rise to the top of your district's search results — pick a period below.",
  "manage.promote.offer": "Offer price {price} (normally {normal}) — {hours} hours left",
  "manage.promote.daysTop": "{days} days on top",
  "manage.promote.newOwnerOffer": "new owner offer",
  "manage.promote.pay": "Pay {price}",
  "manage.promote.note": "Pay once via FPX — no recurring subscription.",
  "manage.bundle.badge": "Bundle package — save {saving}",
  "manage.bundle.title": "Featured {label} + Gallery of {photos} photos = ",
  "manage.bundle.body":
    "Bought separately {separate} ({featured} + {gallery}). The gallery stays unlocked forever, even after Featured ends.",
  "manage.bundle.cta": "Get the bundle for {price}",
  "manage.gallery.title": "Photo gallery",
  "manage.gallery.unlocked": "Gallery unlocked — up to {limit} photos. Photos appear on your public page.",
  "manage.gallery.locked": "Free plan: {free} photo(s). Upgrade for up to {paid} photos.",
  "manage.gallery.usage": "{used} / {limit} photos used",
  "manage.gallery.add": "Add photo",
  "manage.gallery.upgradeTitle": "Gallery upgrade — {price} one-off",
  "manage.gallery.upgradeBody":
    "Unlock up to {photos} photos for this listing (forever). Listings with photos look more trustworthy and get more WhatsApp clicks.",
  "manage.gallery.upgradeCta": "Pay {price} & unlock gallery",
  "manage.gallery.empty": "No photos yet. Add photos to attract more customers.",
  "manage.gallery.main": "Main",
  "manage.gallery.remove": "Remove photo",
  "manage.gallery.moveLeft": "Move left",
  "manage.gallery.moveRight": "Move right",
  "manage.gallery.photoAlt": "Photo {n}",
  "manage.form.phone": "Phone Number",
  "manage.form.category": "Category",
  "manage.form.state": "State",
  "manage.form.district": "Town / City",
  "manage.form.address": "Address (Unit No. & Street Name)",
  "manage.form.postcode": "Postcode",
  "manage.form.addressNote": "Your address and postcode are used to pin your exact location on the map.",
  "manage.form.map": "Location on the map",
  "manage.form.description": "Description",
  "manage.form.save": "Save changes",
  "manage.toast.saved": "Your listing has been updated.",
  "manage.toast.photoAdded": "Photo added.",
  "manage.toast.photoRemoved": "Photo removed.",
  "manage.toast.renewing": "Opening the payment page to extend Featured…",

  "pay.success.title": "Payment successful!",
  "pay.fail.title": "Payment was not completed",
  "pay.success.body":
    "Your FPX payment via toyyibPay was received. Your business listing is now LIVE in the directory. A receipt has been emailed to you.",
  "pay.fail.body": "Your toyyibPay bill has not been paid. You can resume payment without filling in the form again.",
  "pay.billId": "Bill ID",
  "pay.viewDir": "View Directory",
  "pay.resume": "Resume payment",
  "pay.backForm": "Back to the form",

  "resume.invalid": "Invalid link",
  "resume.newSubmit": "Register a new business",
  "resume.live.title": "Your listing is already LIVE",
  "resume.pending.title": "Resume your payment",
  "resume.live.body": "No payment needed. Your listing is already visible in the directory.",
  "resume.pending.body": "No need to fill in the form again — just complete payment to publish your listing.",
  "resume.row.business": "Business",
  "resume.row.category": "Category",
  "resume.row.location": "Location",
  "resume.row.amount": "Amount",
  "resume.row.status": "Status",
  "resume.viewListing": "View your listing",
  "resume.pay": "Pay {price} via toyyibPay",
  "resume.browse": "Browse the directory",
  "resume.toast.live": "Your listing is now LIVE!",

  "map.hint": "Choose a state and town first to show the map.",
  "map.search": "Search a town name (e.g. Alor Setar)",
  "map.toast.short": "Type at least 3 characters.",
  "map.toast.notFound": "Place not found. Try another name.",
  "map.toast.found": "Location found — drag the pin if needed.",
  "map.searching": "Finding the location…",
  "map.failed": "The map could not find this address. You can still submit the form without a pin.",
  "map.adjust": "Drag the pin to your premises if the location shown is not accurate.",
  "map.location": "Location",
  "map.directions": "Get directions (Google Maps)",
  "map.loading": "Loading the map…",
  "map.unavailable": "The map could not load for this location. Try opening it in an external map.",
  "map.searchBtn": "Search",
  "submit.seo.title": "List Your Business — DirektoriBiz.my",
  "submit.seo.desc": "List your business on DirektoriBiz.my — a short form, AI document scan and a single payment.",
  "claim.seo.title": "Access Your Listing — DirektoriBiz.my",
  "claim.seo.desc": "Owners can access their listing to update details or buy an upgrade package.",
  "manage.seo.title": "Manage Listing — DirektoriBiz.my",
  "pay.seo.title": "Payment Result — DirektoriBiz.my",
  "resume.seo.title": "Resume Payment — DirektoriBiz.my",
  "claimcard.seo.title": "Claim a Listing — DirektoriBiz.my",
  "seo.notFound.title": "Unknown location",
  "seo.notFound.body": "This category or district link is not valid. Try searching from the directory page.",
  "seo.related.categories": "Other categories in {place}",
  "seo.related.districts": "Other districts in {state}",
  "seo.browse.title": "Browse the directory by location",
  "directory.nearby.title": "Nearby in the {district} district",
  "directory.nearby.body": "Businesses in neighbouring towns that share the same district.",
  "seo.browse.body": "A dedicated page for every Malaysian state and district — pick your area.",
  "legal.updated": "Last updated",
  "legal.regno": "Registration No.",
  "legal.email": "Email",
};

const DICT = { ms: MS, en: EN };

export const useT = () => {
  const lang = useLang();
  return (key, vars) => {
    let text = DICT[lang][key] ?? DICT.ms[key] ?? key;
    if (vars) {
      Object.entries(vars).forEach(([k, v]) => {
        text = text.replaceAll(`{${k}}`, String(v));
      });
    }
    return text;
  };
};
